/**
 * PillarInferenceView.tsx - Pillar-Centric Inference Display
 *
 * Answers the user's second question: "Why these pillars?"
 * Groups inference chains by strategic pillars (when available) or by
 * BSC perspective flow to show how recommendations were derived.
 *
 * Per wobbly-nibbling-chipmunk.md Phase 2
 * Per REASONING_ORACLE_PROTOTYPE_SELECTION_v3.md Section 12.3
 */

import { useMemo } from 'react'
import type { InferenceChain, BSCPerspective, ConfidenceResult } from '@models/oracle'
import type { CenterTab } from '@contexts/TabNavigationContext'
import './PillarInferenceView.css'

// MECE Pillar structure (from Strategy Workspace)
interface MECEPillar {
  name: string
  description?: string
  supporting_node_ids?: string[]
}

interface PillarInferenceViewProps {
  inferenceChains: InferenceChain[]
  confidence: ConfidenceResult
  mecePillars?: MECEPillar[] // Optional - from Strategy Workspace
  onNavigateToTab?: (tab: CenterTab) => void
  onPillarClick?: (pillarName: string) => void
}

// BSC perspective display config
const BSC_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  Learning_Growth: { label: 'Learning & Growth', color: '#3b82f6', icon: '📚' },
  Internal_Process: { label: 'Internal Process', color: '#10b981', icon: '⚙️' },
  Customer: { label: 'Customer', color: '#f59e0b', icon: '👤' },
  Financial: { label: 'Financial', color: '#ef4444', icon: '💰' },
}

// Format perspective for display
function formatPerspective(perspective: BSCPerspective | string): string {
  return BSC_CONFIG[perspective]?.label || perspective.replace(/_/g, ' ')
}

// Get perspective color
function getPerspectiveColor(perspective: BSCPerspective | string): string {
  return BSC_CONFIG[perspective]?.color || '#6b7280'
}

// Get perspective icon
function getPerspectiveIcon(perspective: BSCPerspective | string): string {
  return BSC_CONFIG[perspective]?.icon || '📊'
}

// Derive perspective from node ID (heuristic based on common patterns)
function derivePerspectiveFromNode(nodeId: string): BSCPerspective | string {
  const node = nodeId.toLowerCase()

  // Financial indicators
  if (node.includes('revenue') || node.includes('cost') || node.includes('profit') ||
      node.includes('financial') || node.includes('cash') || node.includes('margin') ||
      node.includes('receivable') || node.includes('capital') || node.includes('asset')) {
    return 'Financial'
  }

  // Customer indicators
  if (node.includes('customer') || node.includes('retention') || node.includes('satisfaction') ||
      node.includes('loyalty') || node.includes('churn') || node.includes('acquisition') ||
      node.includes('lifetime') || node.includes('market_share')) {
    return 'Customer'
  }

  // Internal Process indicators
  if (node.includes('process') || node.includes('quality') || node.includes('efficiency') ||
      node.includes('operation') || node.includes('service') || node.includes('delivery') ||
      node.includes('productivity') || node.includes('cycle')) {
    return 'Internal_Process'
  }

  // Learning & Growth (default for human/info capital, training, etc.)
  if (node.includes('training') || node.includes('employee') || node.includes('human') ||
      node.includes('information') || node.includes('organization') || node.includes('culture') ||
      node.includes('skill') || node.includes('capability') || node.includes('learning')) {
    return 'Learning_Growth'
  }

  return 'Internal_Process' // Default
}

// Group chains by BSC perspective for strategic display
interface PerspectiveGroup {
  perspective: BSCPerspective | string
  nodeIds: string[]
  chainCount: number
  confidence: number
}

function groupByPerspective(chains: InferenceChain[]): PerspectiveGroup[] {
  const perspectiveMap = new Map<string, { nodeIds: Set<string>; chainCount: number }>()

  chains.forEach((chain) => {
    chain.matched_nodes.forEach((nodeId) => {
      const perspective = derivePerspectiveFromNode(nodeId)
      if (!perspectiveMap.has(perspective)) {
        perspectiveMap.set(perspective, { nodeIds: new Set(), chainCount: 0 })
      }
      const group = perspectiveMap.get(perspective)!
      group.nodeIds.add(nodeId)
    })

    // Count chain contribution
    const primaryPerspective = derivePerspectiveFromNode(chain.anchor_concept)
    if (perspectiveMap.has(primaryPerspective)) {
      perspectiveMap.get(primaryPerspective)!.chainCount++
    }
  })

  // BSC order: L&G -> Process -> Customer -> Financial
  const bscOrder = ['Learning_Growth', 'Internal_Process', 'Customer', 'Financial']

  return bscOrder
    .filter((p) => perspectiveMap.has(p))
    .map((perspective) => {
      const group = perspectiveMap.get(perspective)!
      return {
        perspective,
        nodeIds: Array.from(group.nodeIds),
        chainCount: group.chainCount,
        confidence: 0.85 + Math.random() * 0.10, // Placeholder - would come from actual calculation
      }
    })
}

// Group chains by MECE pillar (when pillar data is available)
interface PillarGroup {
  pillar: MECEPillar
  supportingChains: InferenceChain[]
  supportingNodes: string[]
  confidence: number
}

function groupByPillar(chains: InferenceChain[], pillars: MECEPillar[]): PillarGroup[] {
  return pillars.map((pillar) => {
    const pillarNodeIds = new Set(pillar.supporting_node_ids || [])

    // Find chains that have matching nodes
    const supportingChains = chains.filter((chain) =>
      chain.matched_nodes.some((nodeId) => pillarNodeIds.has(nodeId))
    )

    // Get all supporting nodes from those chains
    const supportingNodes = Array.from(
      new Set(
        supportingChains.flatMap((chain) =>
          chain.matched_nodes.filter((nodeId) => pillarNodeIds.has(nodeId))
        )
      )
    )

    // Calculate confidence based on chain coverage
    const coverage = supportingNodes.length / Math.max(pillarNodeIds.size, 1)
    const confidence = Math.min(0.95, 0.70 + coverage * 0.25)

    return {
      pillar,
      supportingChains,
      supportingNodes,
      confidence,
    }
  }).filter((group) => group.supportingChains.length > 0)
}

function PillarInferenceView({
  inferenceChains,
  confidence: _confidence,
  mecePillars,
  onNavigateToTab,
  onPillarClick,
}: PillarInferenceViewProps) {
  // Group by pillars if available, otherwise by BSC perspective
  const hasPillars = mecePillars && mecePillars.length > 0

  const pillarGroups = useMemo(
    () => (hasPillars ? groupByPillar(inferenceChains, mecePillars!) : []),
    [inferenceChains, mecePillars, hasPillars]
  )

  const perspectiveGroups = useMemo(
    () => (!hasPillars ? groupByPerspective(inferenceChains) : []),
    [inferenceChains, hasPillars]
  )

  // Calculate total matched nodes
  const totalMatchedNodes = useMemo(() => {
    const allNodes = new Set(inferenceChains.flatMap((c) => c.matched_nodes))
    return allNodes.size
  }, [inferenceChains])

  if (inferenceChains.length === 0) {
    return (
      <div className="pillar-inference-view empty">
        <div className="empty-icon">🔍</div>
        <p>No inference chains available to analyze.</p>
      </div>
    )
  }

  return (
    <div className="pillar-inference-view">
      {/* Section Header */}
      <div className="pillar-view-header">
        <h3>
          {hasPillars ? 'How Your Pillars Were Derived' : 'Strategic Evidence by BSC Perspective'}
        </h3>
        <span className="evidence-count">
          {totalMatchedNodes} validated concepts across {inferenceChains.length} chains
        </span>
      </div>

      {/* Pillar-Based View (when MECE pillars available) */}
      {hasPillars && (
        <div className="pillar-groups">
          {pillarGroups.map((group, idx) => (
            <div key={idx} className="pillar-card">
              <div className="pillar-header">
                <div className="pillar-title">
                  <span className="pillar-icon">📋</span>
                  <h4>{group.pillar.name}</h4>
                </div>
                <span
                  className={`pillar-confidence ${
                    group.confidence >= 0.85 ? 'high' : group.confidence >= 0.70 ? 'medium' : 'low'
                  }`}
                >
                  {Math.round(group.confidence * 100)}% confidence
                </span>
              </div>

              {group.pillar.description && (
                <p className="pillar-description">{group.pillar.description}</p>
              )}

              <div className="pillar-evidence">
                <span className="evidence-label">Supporting Evidence:</span>
                <div className="evidence-nodes">
                  {group.supportingNodes.slice(0, 5).map((nodeId) => (
                    <span key={nodeId} className="evidence-node">
                      {nodeId.replace(/_id$/, '').replace(/_/g, ' ')}
                    </span>
                  ))}
                  {group.supportingNodes.length > 5 && (
                    <span className="evidence-more">
                      +{group.supportingNodes.length - 5} more
                    </span>
                  )}
                </div>
              </div>

              <div className="pillar-actions">
                <button
                  className="pillar-action-btn"
                  onClick={() => onPillarClick?.(group.pillar.name)}
                >
                  View Tactics for this Pillar →
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* BSC Perspective View (fallback when no pillars) */}
      {!hasPillars && (
        <div className="perspective-flow">
          <p className="perspective-intro">
            Causeway validated evidence across these BSC perspectives, forming the causal
            foundation for your strategic recommendations:
          </p>

          <div className="perspective-groups">
            {perspectiveGroups.map((group, idx) => (
              <div key={group.perspective} className="perspective-card">
                <div
                  className="perspective-header"
                  style={{ borderLeftColor: getPerspectiveColor(group.perspective) }}
                >
                  <span className="perspective-icon">
                    {getPerspectiveIcon(group.perspective)}
                  </span>
                  <h4>{formatPerspective(group.perspective)}</h4>
                  <span className="node-count">{group.nodeIds.length} concepts</span>
                </div>

                <div className="perspective-nodes">
                  {group.nodeIds.slice(0, 4).map((nodeId) => (
                    <span
                      key={nodeId}
                      className="node-tag"
                      style={{
                        backgroundColor: `${getPerspectiveColor(group.perspective)}20`,
                        borderColor: `${getPerspectiveColor(group.perspective)}40`,
                      }}
                    >
                      {nodeId.replace(/_id$/, '').replace(/_/g, ' ')}
                    </span>
                  ))}
                  {group.nodeIds.length > 4 && (
                    <span className="node-more">+{group.nodeIds.length - 4} more</span>
                  )}
                </div>

                {/* Flow arrow to next perspective */}
                {idx < perspectiveGroups.length - 1 && (
                  <div className="flow-arrow">
                    <span>↓</span>
                    <span className="flow-label">enables</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Link to Strategy Workspace */}
          {onNavigateToTab && (
            <div className="perspective-actions">
              <button
                className="view-pillars-btn"
                onClick={() => onNavigateToTab('workspace')}
              >
                View MECE Pillars in Strategy Workspace →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default PillarInferenceView
